import { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle, GitCompare } from 'lucide-react';
import { authenticatedAxios } from '../services/axiosConfig';
import { TextComparisonDialog, ComparisonItem } from './TextComparisonDialog';
import { SystemPrompt } from '../types/systemPrompt';

interface SystemPromptViewerProps {
  hash: string;
  agentType: string;
  onClose: () => void;
}

interface SystemPromptSnapshot {
  hash: string;
  agentType: string;
  systemPrompt: string;
  indexedAt: string;
}

export const SystemPromptViewer = ({ hash, agentType, onClose }: SystemPromptViewerProps) => {
  const [prompt, setPrompt] = useState<SystemPromptSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonItems, setComparisonItems] = useState<ComparisonItem[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  useEffect(() => {
    loadSystemPrompt();
  }, [hash]);

  const loadSystemPrompt = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedAxios.get<SystemPromptSnapshot>(
        `/ExecutionHistory/system-prompt/${hash}`
      );
      setPrompt(response.data);
    } catch (err) {
      console.error('Failed to load system prompt:', err);
      setError(err instanceof Error ? err.message : 'Failed to load system prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (prompt?.systemPrompt) {
      try {
        await navigator.clipboard.writeText(prompt.systemPrompt);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const openComparison = async () => {
    setLoadingVersions(true);
    try {
      const response = await authenticatedAxios.get<SystemPrompt[]>(
        '/evaluation/system-prompts/full',
        { params: { agentType } }
      );
      const items: ComparisonItem[] = response.data.map(p => ({
        id: p.hash,
        label: p.description || p.hash.substring(0, 8),
        description: p.purpose,
        content: p.content,
        createdAt: p.createdAt,
      }));

      // Ensure current prompt is in the list (it may come from ExecutionHistory, not evaluation)
      if (!items.find(i => i.id === hash) && prompt) {
        items.unshift({
          id: prompt.hash,
          label: `Current (${prompt.hash.substring(0, 8)})`,
          content: prompt.systemPrompt,
          createdAt: prompt.indexedAt,
        });
      }

      setComparisonItems(items);
      setShowComparison(true);
    } catch (err) {
      console.error('Failed to load versions for comparison:', err);
    } finally {
      setLoadingVersions(false);
    }
  };

  return (
    <div data-component="SystemPromptViewer" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">System Prompt</h2>
            <span className="text-sm text-gray-500">({agentType})</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openComparison}
              disabled={isLoading || !!error || loadingVersions}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Compare with other versions"
            >
              <GitCompare className="w-4 h-4" />
              {loadingVersions ? 'Loading...' : 'Compare'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div data-flow="loading" className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading system prompt...</div>
            </div>
          ) : error ? (
            <div data-flow="error" className="flex flex-col items-center justify-center h-64 text-red-600">
              <AlertTriangle className="w-16 h-16 mb-4" />
              <p>{error}</p>
            </div>
          ) : prompt ? (
            <div data-flow="loaded">
              {/* Hash Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs font-medium text-gray-600 mb-1">Hash</div>
                <div className="font-mono text-sm text-gray-800">{prompt.hash}</div>
                <div className="text-xs text-gray-500 mt-2">
                  Indexed: {new Date(prompt.indexedAt).toLocaleString()}
                </div>
              </div>

              {/* System Prompt Text */}
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                  {prompt.systemPrompt}
                </pre>
              </div>

              {/* Copy Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          ) : (
            <div data-flow="no-data" className="flex items-center justify-center h-64 text-gray-500">
              <p>No system prompt data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Dialog */}
      {showComparison && (
        <TextComparisonDialog
          isOpen={showComparison}
          title={`Compare System Prompts (${agentType})`}
          items={comparisonItems}
          initialLeftId={hash}
          lockedLeftId={hash}
          hideApprove={true}
          onApprove={() => {}}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
};
